import React, { useState, useEffect } from 'react';
import { getImageUrlCached } from '../services/imageService';

interface S3ImageProps {
  workspaceId: string;
  imageId: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Image component that automatically fetches presigned URLs from S3
 * Caches URLs to minimize API calls
 */
const S3Image: React.FC<S3ImageProps> = ({
  workspaceId,
  imageId,
  alt,
  className,
  style,
  onLoad,
  onError
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchImageUrl = async () => {
      if (!workspaceId || !imageId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        const url = await getImageUrlCached(workspaceId, imageId);

        if (mounted) {
          setImageUrl(url);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
          onError?.();
        }
      }
    };

    fetchImageUrl();

    return () => {
      mounted = false;
    };
  }, [workspaceId, imageId, onError]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6'
        }}
      >
        <div
          style={{
            width: '24px',
            height: '24px',
            border: '2px solid #e5e7eb',
            borderTopColor: '#9ca3af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          color: '#9ca3af'
        }}
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onLoad={onLoad}
      onError={() => {
        setError(true);
        onError?.();
      }}
    />
  );
};

export default S3Image;
